import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  LinkWrap,
  EmptyColumn,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";
import PlayerLoader from "../../components/PlayerLoader";

import { fetchSubscriptionStatus } from "../../pages/player/api";

// /subscription/success — landing page Stripe redirects to after the
// hosted Checkout flow completes. Polls /subscription/status until the
// webhook flips us to active (typically <2s, max ~10s in the unlikely
// case of webhook lag), then routes to /player/dashboard. On timeout,
// shows a "we're processing your payment" copy with support contact.
//
// PRD §11.7.

const POLL_INTERVAL_MS = 1_000;
const POLL_TIMEOUT_MS = 10_000;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;

    let cancelled = false;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await fetchSubscriptionStatus();
        const status = res?.data?.status;
        if (status === "active") {
          navigate("/player/dashboard", { replace: true });
          return;
        }
      } catch {
        // 401 means JWT lapsed; fall through to timeout copy.
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (!timedOut) {
    return (
      <AuthWrapper>
        <FormWrapper>
          <ContentLogin>
            <EmptyColumn />
            <Form as="div">
              <a href="/">
                <img className="logo" src={logo} alt="Logo" />
              </a>
              <h1>Confirming your payment…</h1>
              <p>One moment — Stripe is finalizing your subscription.</p>
              <PlayerLoader label="confirming payment" />
            </Form>
            <LinkWrap />
          </ContentLogin>
        </FormWrapper>
        <ImageWrapper />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form as="div">
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>We're processing your payment</h1>
            <p>
              Your card was charged but our system hasn't received the
              confirmation yet. We'll email you when it's confirmed (usually
              within a few minutes). You'll be able to access your dashboard
              then.
            </p>
          </Form>
          <LinkWrap>
            <h3>
              If this doesn't resolve in 15 minutes, email{" "}
              <a href="mailto:support@smrtstats.com">support@smrtstats.com</a>{" "}
              and mention your email — we'll sort it out.
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default SubscriptionSuccess;
